import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, CalendarCheck, ScanSearch } from "lucide-react";
import { BUSINESS, track } from "@/components/site/LocalBits";

// Marketing pages only: the bar would get in the way of a form or the customer portal.
const MARKETING = ["/services", "/advice", "/areas-covered", "/about", "/reviews", "/online-tools", "/surveys"];

const isMarketing = (path) =>
  path === "/" || MARKETING.some((base) => path === base || path.startsWith(`${base}/`));

export const showsActionBar = isMarketing;

// Location pages record which action a visitor took, so keep the slug where there is one.
const locationSlug = (path) => {
  const parts = path.split("/").filter(Boolean);
  return parts[0] === "areas-covered" ? parts[2] || parts[1] || "" : "";
};

const ITEM = "flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-lg text-[11px] font-semibold";

/** Phone-only action bar: the three things a visitor on a mobile usually wants, always in reach. */
export default function MobileActionBar() {
  const { pathname } = useLocation();
  const bar = useRef(null);
  const show = isMarketing(pathname);

  // The measured height keeps the footer, the WhatsApp button and the page content clear of it.
  useEffect(() => {
    const root = document.documentElement;
    const measure = () => {
      root.style.setProperty("--pg-actionbar-height", bar.current ? `${bar.current.offsetHeight}px` : "0px");
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      root.style.setProperty("--pg-actionbar-height", "0px");
    };
  }, [show, pathname]);

  if (!show) return null;
  const slug = locationSlug(pathname);

  return (
    <div
      ref={bar}
      data-testid="sticky-action-bar"
      style={{ bottom: "var(--pg-consent-height, 0px)" }}
      className="fixed inset-x-0 z-40 border-t border-pg-border bg-white/95 px-3 pt-2 backdrop-blur
                 shadow-[0_-4px_16px_rgba(15,32,58,0.08)] pb-[max(0.5rem,env(safe-area-inset-bottom))] md:hidden"
    >
      <div className="grid grid-cols-4 gap-2">
        <a
          href={BUSINESS.phoneHref}
          onClick={() => track(slug, "call")}
          data-testid="sticky-call"
          className={`${ITEM} bg-pg-primary text-white`}
        >
          <Phone className="h-[18px] w-[18px]" /> Call
        </a>
        <a
          href={BUSINESS.whatsapp}
          target="_blank"
          rel="noreferrer"
          onClick={() => track(slug, "whatsapp")}
          data-testid="sticky-whatsapp"
          className={`${ITEM} border border-pg-border text-pg-dark`}
        >
          <MessageCircle className="h-[18px] w-[18px]" /> WhatsApp
        </a>
        <Link
          to="/book-an-inspection"
          onClick={() => track(slug, "booking")}
          data-testid="sticky-book"
          className={`${ITEM} border border-pg-border text-pg-dark`}
        >
          <CalendarCheck className="h-[18px] w-[18px]" /> Book
        </Link>
        <Link
          to="/tools/check-my-damp"
          onClick={() => track(slug, "damp_checker")}
          data-testid="sticky-ai"
          className={`${ITEM} border border-pg-border text-pg-dark`}
        >
          <ScanSearch className="h-[18px] w-[18px]" /> AI Check
        </Link>
      </div>
    </div>
  );
}
